import lodashIsNumber from 'lodash-es/isNumber';
import type { ComputedRef, Ref } from 'vue';

import { type AuctionItemEntity, type AuctionListingItemEntity, AuctionState, BackendErrorCode } from '~/apiClient';
import type { InspectionExpiryStatus, SaleStatus } from '~/types/saleStatus';

interface UseAuctionStatusReturn extends SaleStatus {
    isCountdownVisible: ComputedRef<boolean>;
    activeAutoBidAmount: ComputedRef<number | undefined>;
}

type AuctionStatusColorScheme = 'green' | 'yellow' | 'blue' | 'neutral';

export default function useAuctionStatus(auction: Ref<AuctionItemEntity | AuctionListingItemEntity>): UseAuctionStatusReturn {
    const { tt } = useTypedI18n();
    const { companyScopeIds } = useAuthUtils();
    const { extendNegotiation } = useExtendedNegotiation();
    const dayjs = useDayjs();

    const userMeta = computed(() => auction.value.userMeta ?? null);
    const extendedNegotiation = computed(
        () =>
            auction.value.negotiation &&
            extendNegotiation(auction.value.negotiation, 'canActForSellerCompany' in auction.value ? auction.value.canActForSellerCompany : undefined)
    );

    // Indicates whether the auction or negotiation is awarded for buyer or seller users. Always false for third-party users or guests.
    const isAwardedForCurrentUser = computed(() => {
        if (auction.value.clearingPrice === null) return false;

        return (
            isSeller.value ||
            !!userMeta.value?.maxBidder ||
            !!extendedNegotiation.value?.isSuccessful ||
            ('orderId' in auction.value && !!auction.value.orderId)
        );
    });

    const isPriceHidden = computed(() => !lodashIsNumber(auction.value.currentPrice));

    const isFeeNoticeHidden = computed(
        () =>
            isPriceHidden.value ||
            !auction.value.state ||
            !(<AuctionState[]>[AuctionState.Waiting, AuctionState.Pending, AuctionState.Running, AuctionState.Expired]).includes(auction.value.state)
    );

    const isCountdownVisible = computed(() => auction.value.state === AuctionState.Pending || auction.value.state === AuctionState.Running);

    const colorScheme = computed<AuctionStatusColorScheme>(() => {
        if (auction.value.state === AuctionState.Running) {
            if (userMeta.value?.maxBidder) return 'green';
            if (userMeta.value?.outBid) return 'yellow';

            return 'blue';
        }

        if (isAwardedForCurrentUser.value) return 'green';

        if (extendedNegotiation.value?.isInProgress) return 'blue';

        return 'neutral';
    });

    const statusSectionColor = computed(() => {
        const colors: Record<AuctionStatusColorScheme, string> = {
            green: 'bg-success-50',
            yellow: 'bg-amber-50',
            blue: 'bg-primary-50',
            neutral: 'bg-neutral-100',
        } as const;

        return colors[colorScheme.value];
    });

    const title = computed(() => {
        if (auction.value.soldOffPlatform) return tt('sale.soldOffPlatform');

        switch (auction.value.state) {
            case AuctionState.Draft:
            case AuctionState.ToBeApproved:
            case AuctionState.ToBeScheduled:
                return tt('sdp.Auction.auctionStatus.sellerReservePrice');
            case AuctionState.Pending:
                return tt('sdp.Auction.auctionStatus.startingPrice');
            case AuctionState.Running:
                return tt('sdp.Auction.auctionStatus.current');
            case AuctionState.Canceled:
                return tt(`sdp.Auction.state.${AuctionState.Canceled}`);
            case AuctionState.Expired:
                return isAwardedForCurrentUser.value ? tt('sdp.Auction.auctionStatus.wonAuction') : tt(`sdp.Auction.state.${AuctionState.Expired}`);
        }
    });

    const titleColor = computed(() => {
        if (isAwardedForCurrentUser.value) return 'text-success-600';
    });

    const titleAnnotation = computed(() => {
        if (auction.value.state === AuctionState.Running) {
            if (userMeta.value?.maxBidder) {
                return tt(userMeta.value.colleagueMaxBidder ? 'sdp.Auction.auctionStatus.colleagueMaxBidder' : 'sdp.Auction.auctionStatus.maxBidder');
            }

            if (userMeta.value?.outBid) return tt('sdp.Auction.auctionStatus.outBid');
        }

        if (extendedNegotiation.value?.isInProgress) return tt('sdp.Auction.auctionStatus.negotiationInProgress');
    });

    const titleAnnotationColor = computed(() => {
        const colors: Partial<Record<AuctionStatusColorScheme, string>> = {
            green: 'text-success-600',
            yellow: 'text-warning-600',
            blue: 'text-primary-600',
        } as const;

        return colors[colorScheme.value];
    });

    const priceColor = computed(() => {
        if (isPriceHidden.value) return 'text-default';

        if (auction.value.state === AuctionState.Expired) return isAwardedForCurrentUser.value ? 'text-success-800' : 'text-default';

        return 'text-primary';
    });

    const isBailNeeded = computed(() => !userMeta.value?.canBid && userMeta.value?.cantBidReason === BackendErrorCode.CantPerformUserNoBail);

    const inspectionExportBlockReason = computed<InspectionExpiryStatus | undefined>(() => {
        if (userMeta.value?.canBid) return undefined;

        const reason = userMeta.value?.cantBidReason;

        if (reason === BackendErrorCode.CantPerformInspectionExpiredNotExportable) return 'expired';
        if (reason === BackendErrorCode.CantPerformInspectionExpiringSoonNotExportable) return 'expiringSoon';

        return undefined;
    });

    const hasInspectionExportBlock = computed(() => !!inspectionExportBlockReason.value);

    const hasRestrictedNotice = computed(
        () =>
            !!userMeta.value &&
            !userMeta.value?.canBid &&
            userMeta.value?.cantBidReason !== BackendErrorCode.CantBidAuctionIsNotRunning &&
            !isBailNeeded.value &&
            !hasInspectionExportBlock.value
    );

    const hasInspectionNotice = computed(() => !!userMeta.value?.inspectionExpiredExportRestrictions && !hasInspectionExportBlock.value);

    const activeAutoBidAmount = computed<number | undefined>(() => (userMeta.value?.autoBid?.active ? userMeta.value?.autoBid?.amount : undefined));

    const isSeller = computed(() => !!auction.value && isSellerOfSale(auction.value, companyScopeIds.value));

    const inspectionReminder = computed<InspectionExpiryStatus | undefined>(() => {
        if (!isSeller.value) return;

        if (!auction.value.mainAsset.inspectionExpiryDate) return;

        const now = dayjs();
        const target = dayjs(auction.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        isFeeNoticeHidden,
        isCountdownVisible,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        hasInspectionExportBlock,
        inspectionExportBlockReason,
        activeAutoBidAmount,
        isSeller,
        inspectionReminder,
    };
}
